import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useRef } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import {
  COLOR_BOTON_POR_DEFECTO,
  colorSeguro,
  tintaSobre,
} from '@claimup/shared/helpers/email-button-color';
// Registra el blot del boton: tiene que cargarse antes de montar el editor.
import './botonBlot';

/**
 * El cuerpo de una plantilla de correo, con formato.
 *
 * **Hacia fuera no habla de HTML, habla de operaciones**: lo que sale por
 * `onChange` es la estructura del editor (`[{ insert, attributes }]`), que es lo
 * que se guarda. El HTML del correo lo monta el backend a partir de eso. Así el
 * texto plano del correo sale de la misma estructura que el HTML —ninguno es una
 * conversión del otro— y no hay HTML de fuera que sanear en el servidor.
 *
 * La barra está recortada a lo que se ve igual en todos los clientes de correo.
 * Lo que NO está, y es a propósito:
 *   - **la fuente**, que va en la marca de la empresa y no por plantilla: las
 *     fuentes web las ignoran Gmail en Android, Outlook de escritorio y Yahoo,
 *     y una fuente por plantilla haría que sus correos no se parezcan entre sí;
 *   - imágenes y vídeo, que son bloques y vendrán como tales, no incrustados.
 */

const BARRA = [
  ['bold', 'italic', 'underline'],
  [{ size: ['small', false, 'large', 'huge'] }],
  [{ color: [] }],
  [{ list: 'ordered' }, { list: 'bullet' }],
  [{ align: '' }, { align: 'center' }, { align: 'right' }, { align: 'justify' }],
  ['link'],
  ['clean'],
];

/**
 * Lo que el editor puede aplicar. Es la misma lista que valida el backend, y
 * además es lo que limpia el pegado: al pegar de Word, Quill descarta lo que no
 * esté aquí en vez de arrastrar su marcado.
 */
const FORMATOS = ['bold', 'italic', 'underline', 'size', 'color', 'list', 'align', 'link', 'button'];

/**
 * Quita los atributos apagados antes de mandar.
 *
 * Quill marca «alineado a la izquierda» o «sin lista» como `false`/`null` en vez
 * de quitar el atributo. El backend valida contra una lista cerrada de valores y
 * un `align: false` le daría un 400 que la persona no sabría arreglar.
 */
export function limpiarOps(ops) {
  return (Array.isArray(ops) ? ops : [])
    .map(op => {
      const attrs = Object.fromEntries(
        Object.entries(op.attributes || {}).filter(
          ([, v]) => v !== null && v !== false && v !== '' && v !== undefined
        )
      );
      return Object.keys(attrs).length ? { insert: op.insert, attributes: attrs } : { insert: op.insert };
    })
    // Texto, o un boton. Cualquier otro embebido que Quill pudiera meter se
    // queda fuera: el backend tambien lo rechaza, pero mejor no mandarlo.
    .filter(op => typeof op.insert === 'string' || Boolean(op.insert?.button));
}

/** ¿Hay algo escrito? Un editor vacío trae un único salto de línea. */
export function opsVacias(ops) {
  // Un boton cuenta como contenido: una plantilla que solo lleve «Pagar»
  // es rara, pero no esta vacia.
  return !limpiarOps(ops).some(op =>
    typeof op.insert === 'string' ? op.insert.trim() : Boolean(op.insert?.button)
  );
}

/**
 * Cómo se viste el botón incrustado.
 *
 * **Son las medidas del botón del correo, no las de la app**, y por eso aquí hay
 * un `rounded-[6px]` y no el `rounded-lg` de todo lo demás: esto no es un
 * control de la pantalla, es una copia de lo que le llega al deudor
 * (`email-resend.buttons.js`: radio 6, relleno 12/26, 15px y 700). El color y la
 * tinta entran por variables CSS porque el blot de Quill los pinta con `class` y
 * no puede leer el estado de React.
 *
 * 🔴 **Ninguna clase de aquí puede llevar un guion bajo en el nombre.** Dentro
 * de un selector arbitrario de Tailwind el guion bajo **es un espacio**: hasta el
 * 28/10/2026 la cara se llamaba `ar-boton-editor` + dos guiones bajos + `cara`, y
 * el selector compilaba a `.ar-boton-editor cara { … }` — un descendiente de
 * etiqueta `<cara>` que no existe. O sea: **el botón del editor nunca tuvo
 * estilo**. Salían la etiqueta y el destino como dos trozos de texto pelado uno
 * al lado del otro, que es por lo que «se veía raro», y no daba ningún error ni
 * en el build ni en los tests.
 *
 * (Y por eso el nombre viejo no se escribe completo en ningún comentario:
 * Tailwind lee también los comentarios y generaría su CSS muerto.)
 */
const CARA_DEL_BOTON = [
  '[&_.ar-boton-editor]:my-5',
  '[&_.ar-boton-cara]:inline-block',
  '[&_.ar-boton-cara]:rounded-[6px]',
  '[&_.ar-boton-cara]:bg-[var(--ar-boton-fondo)]',
  '[&_.ar-boton-cara]:px-[26px]',
  '[&_.ar-boton-cara]:py-3',
  '[&_.ar-boton-cara]:text-[15px]',
  '[&_.ar-boton-cara]:font-bold',
  '[&_.ar-boton-cara]:leading-none',
  '[&_.ar-boton-cara]:text-[color:var(--ar-boton-tinta)]',
  // Debajo del botón, no al lado: ahí parecía parte del diseño.
  '[&_.ar-boton-destino]:mt-1.5',
  '[&_.ar-boton-destino]:block',
  '[&_.ar-boton-destino]:text-[11px]',
  '[&_.ar-boton-destino]:font-mono',
  '[&_.ar-boton-destino]:text-slate-400',
].join(' ');

/**
 * `color`: el de la marca de la empresa, para que el botón del editor sea el del
 * correo. Sin empresa —el catálogo estándar— sale el azul de ClaimUp, que es con
 * el que el backend lo manda.
 */
const EditorCuerpo = forwardRef(({ valorInicial, onChange, alEnfocar, soloLectura = false, color }, ref) => {
  const quillRef = useRef(null);
  const fondo = colorSeguro(color || COLOR_BOTON_POR_DEFECTO);

  const modules = useMemo(() => ({ toolbar: BARRA }), []);

  // Solo al montar: si se pasara el valor en cada render, Quill recolocaría el
  // cursor al principio a cada tecla.
  const inicial = useMemo(
    () => ({ ops: valorInicial?.length ? valorInicial : [{ insert: '\n' }] }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useImperativeHandle(ref, () => ({
    /**
     * Mete una variable donde está el cursor, no al final.
     *
     * Es la diferencia con el campo de texto de antes: aquí se escribe un correo
     * entero, y pegar «{{amount}}» al final obligaría a cortar y pegar siempre.
     */
    /**
     * Quita el formato que viene pegado de otro sitio.
     *
     * 🔴 Hace falta porque pegar desde un correo ya enviado trae su estilo. El
     * 27/10/2026 un párrafo llegó al deudor **en blanco sobre blanco** —venía de
     * un botón oscuro, con su `color:#ffffff`— y otra línea salió centrada ella
     * sola en medio de un bloque. La maqueta ya descarta los colores que no se
     * leen, pero eso es la última red: mejor que el texto no los traiga.
     *
     * Se van el color, el fondo, el tamaño y la alineación. **Se quedan** la
     * negrita, la cursiva, el subrayado, los enlaces, las listas y los botones:
     * eso es lo que alguien pone a propósito.
     */
    limpiarFormato() {
      const editor = quillRef.current?.getEditor();
      if (!editor) return;

      const DE_PEGADO = ['color', 'background', 'size', 'align'];
      const ops = (editor.getContents().ops || []).map(op => {
        const attrs = { ...(op.attributes || {}) };
        for (const k of DE_PEGADO) delete attrs[k];
        return Object.keys(attrs).length ? { insert: op.insert, attributes: attrs } : { insert: op.insert };
      });

      editor.setContents({ ops });
      onChange?.(limpiarOps(ops));
    },

    /**
     * Mete un boton donde esta el cursor, como un bloque propio.
     *
     * Es lo que permite decidir DONDE cae: justo despues de la frase que lo
     * pide, no al final del correo.
     */
    insertarBoton(boton) {
      const editor = quillRef.current?.getEditor();
      if (!editor) return;

      const donde = editor.getSelection(true)?.index ?? editor.getLength();
      editor.insertEmbed(donde, 'button', boton, 'user');
      // Un salto detras: si no, no hay forma de escribir debajo del boton
      // cuando queda al final del cuerpo.
      editor.insertText(donde + 1, '\n', 'user');
      editor.setSelection(donde + 2, 0);
    },

    insertarVariable(nombre) {
      const editor = quillRef.current?.getEditor();
      if (!editor) return;

      const marca = `{{${nombre}}}`;
      const donde = editor.getSelection(true)?.index ?? editor.getLength();
      editor.insertText(donde, marca, 'user');
      editor.setSelection(donde + marca.length, 0);
    },
  }));

  const cambiar = useCallback(
    (_html, _delta, _source, editor) => {
      onChange(limpiarOps(editor.getContents()?.ops));
    },
    [onChange]
  );

  return (
    <div
      onFocus={alEnfocar}
      // Las variables visten el botón incrustado y van aquí porque el blot de
      // Quill pinta con `class` y no ve el estado de React. El botón del correo
      // de verdad lo monta el backend con tablas, que es lo único que entiende
      // Outlook; esto es la misma cara, con el mismo color.
      style={{ '--ar-boton-fondo': fondo, '--ar-boton-tinta': tintaSobre(fondo) }}
      className={`[&_.ql-container]:rounded-b-lg [&_.ql-toolbar]:rounded-t-lg [&_.ql-editor]:min-h-[180px] [&_.ql-editor]:text-sm [&_.ql-container]:border-slate-200 [&_.ql-toolbar]:border-slate-200 ${CARA_DEL_BOTON}`}
    >
      <ReactQuill
        ref={quillRef}
        theme='snow'
        defaultValue={inicial}
        onChange={cambiar}
        readOnly={soloLectura}
        // Sin barra cuando no se puede escribir: ofrecer botones que no hacen
        // nada es peor que no ofrecerlos.
        modules={soloLectura ? { toolbar: false } : modules}
        formats={FORMATOS}
      />
    </div>
  );
});

EditorCuerpo.displayName = 'EditorCuerpo';

export default EditorCuerpo;
